import { ForgotPassword } from 'aws-amplify-react';
import { Field, Formik } from 'formik';
import { createRef } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import VerificationInput from 'react-verification-input';
import validator from 'validator';
import * as Yup from 'yup';

import withHookedTheme from '../hocs/withHookedTheme';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';

/**
 * Extending amplify boilerplate ForgotPassword screen.
 */
class LoopForgotPassword extends ForgotPassword {
  /**
   * Class constructor
   * @param {Object} props The screen's properties
   */
  constructor(props) {
    super(props);
    this.language = props.theme.language;
    this._validAuthStates = ['forgotPassword'];
    this.onKeyUp = this.onKeyUp.bind(this);

    this.state = {
      resentEmail: false,
    };

    this.validationInputRef = createRef();

    this.preservedUsername = '';
  }

  /**
   * onKeyUp event handler
   * @param {Object} event The event payload
   */
  onKeyUp(event) {
    if (event.charCode === 13) {
      event.preventDefault();
      this.state.delivery || this.state.username ? this.submit() : this.send();
    }
  }

  /**
   * Show send view form
   * @return {JSX.Element}
   */
  sendView() {
    const { loading } = this.state;
    const sendValidationSchema = Yup.object({
      username: Yup.string()
        .email('Invalid email address')
        .required('Email is required')
        .test('email test', 'Invalid email address', (value) => validator.isEmail((value || '').toString())),
    });
    return (
      <Formik
        initialValues={{ username: '' }}
        validationSchema={sendValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          const username = values.username.toLowerCase().trim();
          this.inputs = {
            username,
          };
          this.preservedUsername = username;
          super.send();
          setSubmitting(false);
        }}
        validateOnBlur
        validateOnChange
      >
        {({ handleSubmit, errors, isSubmitting }) => (
          <Form className="text-left" noValidate onSubmit={handleSubmit}>
            <Form.Group>
              <Card.Text>
                Enter the email address you used to register with {this.language.appName}, and we&apos;ll send you
                instructions to reset your password.
              </Card.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="username">Email address</Form.Label>
              <Field name="username">
                {({ field, meta }) => (
                  <>
                    <Form.Control
                      {...field}
                      id="signin_username"
                      key="username"
                      type="email"
                      placeholder="Email address"
                      onChange={(e) => {
                        e.target.value = e.target.value.toLowerCase().trim();
                        field.onChange(e);
                      }}
                      isInvalid={meta.touched && !!meta.error}
                      style={{ backgroundImage: 'none' }}
                    />
                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                  </>
                )}
              </Field>
            </Form.Group>
            <Form.Group>
              <Button
                type="submit"
                variant="primary"
                block
                disabled={loading || isSubmitting || !!Object.keys(errors).length}
              >
                Send Instructions
              </Button>
            </Form.Group>
            <Form.Group className="mb-0">
              <p className="text-center mb-0 mt-3">
                <Button variant="link" onClick={() => super.changeState('signIn')}>
                  Back to Sign In?{' '}
                </Button>
              </p>
            </Form.Group>
          </Form>
        )}
      </Formik>
    );
  }

  /**
   * Show submit view form
   * @return {JSX.Element}
   */
  submitView() {
    const { loading, resentEmail } = this.state;
    const submitValidationSchema = Yup.object({
      code: Yup.string().length(6, 'Code must be 6 digits').required('Code is required'),
      password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
    });
    return (
      <Formik
        initialValues={{ code: '', password: '' }}
        validationSchema={submitValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          this.inputs = {
            username: this.preservedUsername,
            code: values.code,
            password: values.password,
          };
          super.submit();
          setSubmitting(false);
        }}
        validateOnBlur
        validateOnChange
      >
        {({ handleSubmit, errors, isSubmitting }) => (
          <Form className="text-left" noValidate onSubmit={handleSubmit}>
            <Form.Group>
              <Card.Text>
                Please enter the code which we have emailed to you and create a new password that is at least 8
                characters long.
              </Card.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="code">Code</Form.Label>
              <Field name="code">
                {({ field, meta }) => (
                  <>
                    <VerificationInput
                      ref={this.validationInputRef}
                      validChars="0-9"
                      placeholder="0"
                      removeDefaultStyles={true}
                      autoFocus={true}
                      inputField={{
                        name: 'code',
                        value: field.value,
                        onChange: (e) => {
                          field.onChange({
                            target: {
                              name: 'code',
                              value: e.target.value,
                            },
                          });
                        },
                      }}
                      container={{
                        className: 'verification-input-container',
                      }}
                      characters={{
                        className: 'verification-input-characters',
                      }}
                      character={{
                        className: 'verification-input-character',
                        classNameInactive: 'verification-input-character--inactive',
                        classNameSelected: 'verification-input-character--selected',
                      }}
                    />
                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                  </>
                )}
              </Field>
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="password">New Password</Form.Label>
              <Field name="password">
                {({ field, meta }) => (
                  <>
                    <Form.Control
                      {...field}
                      id="forgot_password_new_password"
                      key="password"
                      type="password"
                      placeholder="New Password"
                      isInvalid={meta.touched && !!meta.error}
                      style={{ backgroundImage: 'none' }}
                    />
                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                  </>
                )}
              </Field>
            </Form.Group>
            <Form.Group>
              <Button
                type="submit"
                variant="primary"
                block
                disabled={loading || isSubmitting || !!Object.keys(errors).length}
              >
                Reset Password
              </Button>
            </Form.Group>
            <Form.Group className="mb-0 mt-1">
              {resentEmail ? (
                <p className="text-center mb-0">Email resent with code</p>
              ) : (
                <p className="text-center mb-0 pt-1">
                  Email didn&apos;t arrive?{' '}
                  <Button
                    variant="link"
                    onClick={() => {
                      super.send();
                      this.setState({ resentEmail: true });
                      this.resetInputAndValidationInputComponent();
                    }}
                  >
                    Resend Code{' '}
                  </Button>
                </p>
              )}
              <p className="text-center mb-0 mt-3">
                <Button variant="link" onClick={() => super.changeState('signIn')}>
                  Back to Sign In
                </Button>
              </p>
            </Form.Group>
          </Form>
        )}
      </Formik>
    );
  }

  /**
   * Reset the input and validation
   */
  resetInputAndValidationInputComponent() {
    this.inputs.code = 0;
    this.validationInputRef.current.state.tan = '';
    this.validationInputRef.current.state.previousTan = '';
    this.validationInputRef.current.state.selectedIndex = 0;
    this.validationInputRef.current.state.isActive = false;
    this.validationInputRef.current.state.isValidTan = false;
  }

  /**
   * Show loop forgot password screen
   * @param {Object} theme The theme object
   * @return {JSX.Element}
   */
  showComponent(theme) {
    const { authData = {} } = this.props;
    return (
      <LoginLayout>
        <LoginCard>
          <Card.Title as="h1">Reset Password</Card.Title>
          {this.state.delivery || authData.username ? this.submitView() : this.sendView()}
        </LoginCard>
      </LoginLayout>
    );
  }
}

export default withHookedTheme(LoopForgotPassword);
